import { Component, EventEmitter, inject, Input, isDevMode, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { FilterExpression } from '../services/data.service';
import { FilterService } from '../services/filter.service';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { flatMap, isEqual, sortBy, toPairs } from 'lodash-es';
import { map } from 'rxjs/operators';
import { InstantSearchBoxComponent } from '@cumlaude/shared-components-inputs';
import { AsyncPipe } from '@angular/common';
import { FilterContainerComponent } from './filter-container.component';
import { includesIgnoreCaseAndDiacritics } from '@cumlaude/shared-utils';
import { CategoryFields, getMetadataSearchKeys } from '../services/metadata';
import { FilterName } from '../services/filter-name';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { attOverridesUitRouteData, UserRouteData } from '../services/user-routes.service';

@Component({
	selector: 'app-filter-panel',
	templateUrl: './filter-panel.component.html',
	styleUrls: ['./filter-panel.component.scss'],
	imports: [FilterContainerComponent, InstantSearchBoxComponent, AsyncPipe, IconComponent],
})
export class FilterPanelComponent implements OnChanges, OnDestroy {
	private readonly filterService = inject(FilterService);

	private readonly routeData = toSignal(inject(ActivatedRoute).data);

	private customFiltersGemeld = false;

	@Input()
	defaultFilters: FilterName[] = [];

	@Input()
	allFilters: CategoryFields[] = [];

	@Input()
	leerlingselectie = true;

	@Input()
	permanentFilterExpressions: FilterExpression[] = [];

	@Input()
	overrideDefault?: Partial<{ [name in FilterName]: any }>;

	@Input()
	endpoint!: string;

	@Output()
	filterExpressionsChange = new EventEmitter<FilterExpression[]>();

	@ViewChild('searchBox')
	private readonly searchBox?: InstantSearchBoxComponent;

	defaultTabSelected = true;

	visibleDefaultFilters$: Observable<FilterName[]> = of();

	nonDefaultFilters$: Observable<[string, FilterName[]][]>;

	filterFilter$ = new BehaviorSubject('');

	private readonly subscriptions: Subscription[] = [];

	constructor() {
		this.nonDefaultFilters$ = combineLatest([this.filterService.getRegularActiveFilters(), this.filterFilter$]).pipe(
			map(([active, filterFilter]) => {
				const activeNonDefault: FilterName[] = [];
				const pairs = toPairs(this.filterService.getNonDefaultFilters());
				const newPairs: [string, FilterName[]][] = pairs.map(([categorieLabel, atts]) => [
					categorieLabel,
					sortBy(
						flatMap(atts, (att) => {
							if (!this.matches(filterFilter, att, categorieLabel)) return [];
							if (!active.includes(att)) return [att];
							activeNonDefault.push(att);
							return [];
						}),
						(att) => this.filterService.configs[att]!.label
					),
				]);
				return [['Actieve filters', activeNonDefault], ...newPairs];
			})
		);

		this.subscriptions.push(
			this.filterService.filterPanelOpened$.subscribe((opened) => {
				if (opened) {
					this.defaultTabSelected = true;
					this.filterFilter$.next('');
				} else {
					this.filterService.clearTentative();
				}
			}),
			this.filterService.getFilterExpressions().subscribe((fex) => this.filterExpressionsChange.emit(fex))
		);
	}

	trackByCategory(index: number, cat: [string, FilterName[]]): string {
		return cat[0];
	}

	matches(filterFilter: string, att: FilterName, categorieLabel: string) {
		if (filterFilter === '') return true;

		const filterConfig = this.filterService.configs[att]!;
		return (
			includesIgnoreCaseAndDiacritics(filterConfig.label, filterFilter) ||
			filterConfig.extraSearchKeys.some((k) => includesIgnoreCaseAndDiacritics(k, filterFilter)) ||
			getMetadataSearchKeys(att).some((k) => includesIgnoreCaseAndDiacritics(k, filterFilter)) ||
			includesIgnoreCaseAndDiacritics(categorieLabel, filterFilter)
		);
	}

	ngOnChanges() {
		if (isDevMode()) this.controleerCustomFiltersUitRegistry();

		this.filterService.endpoint = this.endpoint;
		this.filterService.permanentFilterExpressions = this.permanentFilterExpressions;
		const leerlingselectie: FilterName[] = this.leerlingselectie ? ['x_leerlingselectie'] : [];
		this.filterService.setAvailableFilters(
			[...leerlingselectie, ...this.defaultFilters],
			this.allFilters,
			attOverridesUitRouteData(this.routeData()),
			this.overrideDefault
		);
		this.visibleDefaultFilters$ = combineLatest(
			this.filterService.getDefaultFilters().map((f) => this.filterService.isVisible(f).pipe(map((v) => (v ? [f] : []))))
		).pipe(map((namesLists) => flatMap(namesLists)));
	}

	/**
	 * De custom filters in de dashboard-registry zijn een samenvatting voor de AI-chat: ze vertellen het
	 * model welke `x_`-filters dit dashboard heeft en welke waarden ze accepteren. Deze controle bewaakt
	 * dat de samenvatting klopt met de filterconfig, zodat de AI-chat geen verouderde informatie krijgt.
	 * Filters met een eigen variant staan niet in beide lijsten, dus die controleren we alleen op de
	 * opties.
	 */
	private controleerCustomFiltersUitRegistry() {
		const uitRegistry = (this.routeData() as UserRouteData | undefined)?.customFilters;
		if (!uitRegistry || this.customFiltersGemeld) return;

		const problemen = uitRegistry.flatMap((filter) => {
			const config = this.filterService.configs[filter.att as FilterName];
			if (!config) return [`${filter.att} bestaat niet in de filterconfig`];

			const fixedOptions = (config.componentParams as { fixedOptions?: unknown[] } | undefined)?.fixedOptions;
			const onbekendeOpties = Object.values(filter.optiesPerBron ?? {})
				.flat()
				.filter((optie) => fixedOptions && !fixedOptions.includes(optie));
			if (onbekendeOpties.length > 0)
				return [`${filter.att} heeft in de registry opties per bron (${onbekendeOpties.join(', ')}) die de filterconfig niet kent`];

			if (filter.opties && fixedOptions && !isEqual(filter.opties, fixedOptions))
				return [
					`${filter.att} heeft in de registry andere opties (${filter.opties.join(', ')}) dan de filterconfig (${fixedOptions.join(', ')})`,
				];

			if (!filter.variant && !this.defaultFilters.includes(filter.att as FilterName))
				return [`${filter.att} staat niet in de voorgestelde filters van dit dashboard`];

			return [];
		});
		if (problemen.length === 0) return;

		this.customFiltersGemeld = true;
		console.error(
			`Dashboard-registry en dit dashboard verschillen: ${problemen.join('; ')}. Werk de registry bij, anders krijgt de AI-chat verouderde informatie.`
		);
	}

	close() {
		this.filterService.filterPanelOpened$.next(false);
	}

	switchTab() {
		if (this.defaultTabSelected) {
			setTimeout(() => {
				this.searchBox!.focus();
			}, 50);
		} else {
			this.filterFilter$.next('');
			this.filterService.clearTentative();
		}
		this.defaultTabSelected = !this.defaultTabSelected;
	}

	ngOnDestroy() {
		for (const sub of this.subscriptions) sub.unsubscribe();
	}
}
